/**
 * Zunelo Domain Types & Real API Data Contracts
 *
 * Strict, realistic domain definitions for:
 * 1. Jira REST API (v3)
 * 2. GitHub REST / GraphQL API (v4)
 * 3. Slack Web API (conversations.replies, conversations.history)
 * 4. Autonomous Agent Orchestration Protocol
 * 5. Gmail Message API
 * 6. Unified Presentation & Attention Models
 */

// ==========================================
// 0. Base Utility Types & UI Design Tokens
// ==========================================

export type Tone = 'ok' | 'warn' | 'bad' | 'accent' | 'flat';
export type AttentionStatus = 'you' | 'them' | 'blocked' | 'ready';
export type SourceType = 'jira' | 'slack' | 'github' | 'gmail' | 'agents';
export type ViewMode =
  'radar' | 'notifications' | 'jira' | 'slack' | 'github' | 'gmail' | 'agents' | 'wiki';
export type ThemeMode = 'light' | 'dark';

/**
 * Pure domain representation of the currently signed-in user.
 * Kept free of React hooks and Firebase SDK objects so domain mappers remain 100% pure.
 */
export interface CurrentUserIdentity {
  uid: string;
  email?: string;
  displayName?: string;
  providerHandle?: string;
}

/**
 * Pure helper to match an external author/reviewer object against the current user identity.
 */
function normalizePart(value?: string): string {
  return (value || '').trim().toLowerCase();
}

function stripLegacyYouSuffix(name: string): string {
  return name.replace(/\s*\((tú|you)\)$/i, '').trim();
}

function nonEmptyEqual(left: string, right: string): boolean {
  return right.length > 0 && left === right;
}

function matchesAnyLogin(userLogin: string, candidates: string[]): boolean {
  return candidates.some((candidate) => nonEmptyEqual(userLogin, candidate));
}

function matchesDisplayName(userName: string, displayName: string, email: string): boolean {
  if (nonEmptyEqual(userName, displayName)) return true;
  return nonEmptyEqual(userName, email);
}

function emailLocalPart(email: string): string {
  if (!email) return '';
  return email.split('@')[0].trim();
}

export function isMatchingUser(
  user: { login?: string; name?: string } | undefined,
  currentUser?: CurrentUserIdentity,
): boolean {
  if (!user) return false;
  if (!currentUser) return false;
  const userLogin = normalizePart(user.login);
  const userName = stripLegacyYouSuffix(normalizePart(user.name));
  const handle = normalizePart(currentUser.providerHandle);
  const email = normalizePart(currentUser.email);
  const displayName = normalizePart(currentUser.displayName);

  if (matchesAnyLogin(userLogin, [handle, email, emailLocalPart(email), displayName])) return true;
  return matchesDisplayName(userName, displayName, email);
}

// ==========================================
// 1. API Envelope, Pagination & Error Models (sourced from @zunelo/shared)
// ==========================================

export type {
  PageInfo,
  RateLimitInfo,
  ApiEnvelope,
  ApiError,
  AppNotification,
  NotificationSource,
  UserSettingsPublic,
  UserSettingsUpdate,
  AiGenerationSettings,
  SlackVoiceSettings,
  SecretStatus,
  WorkContextGraph,
  ResourceRelation,
  Resource,
  ResourceId,
  WorkContext,
  ManualWorkHub,
  Conversation,
  ConversationSummary,
  ConversationAnchor,
  ConversationMessage,
  ConversationRole,
  CreateConversationInput,
  AddConversationMessageInput,
  UnattendedInboxResponse,
  LlmWikiPage,
  LlmWikiPageList,
  LlmWikiNodeId,
  LlmWikiNodeKind,
  LlmWikiGraphSourceKind,
  LlmWikiGraphNodeKind,
  LlmWikiStructuralRole,
  LlmWikiRelationKind,
  LlmWikiTopologyNode,
  LlmWikiTopologyEdge,
  LlmWikiGraphTotals,
  LlmWikiTopologyManifest,
  LlmWikiNodeMetadata,
  LlmWikiNodeMetadataPage,
  LlmWikiGraphMetrics,
  LlmWikiGraphPosition,
  LlmWikiGraphNode,
  LlmWikiGraphEdge,
  LlmWikiGraph,
  LlmWikiGraphFilter,
  LlmWikiGraphProjection,
  LlmWikiGraphViewport,
} from '@zunelo/shared';

// ==========================================
// 2. Jira Real Domain Contracts
// ==========================================

export type JiraIssueType = 'Story' | 'Bug' | 'Task' | 'Epic' | 'Subtask';

export interface JiraUser {
  accountId: string;
  displayName: string;
  emailAddress: string;
  avatarUrl?: string;
  personId?: string;
}

export interface JiraAcceptanceCriterion {
  id?: string;
  text?: string;
  label?: string;
  status?: 'done' | 'open' | 'blocked' | string;
  state?: 'done' | 'open' | 'blocked' | string;
  completedAt?: string; // ISO 8601
}

export interface JiraSubtask {
  id: string;
  key: string;
  summary: string;
  status: 'Pendiente' | 'En curso' | 'Hecho' | 'Bloqueado';
  assignee?: JiraUser;
}

export interface JiraIssueLink {
  relationship: string; // 'Bloquea', 'Bloqueada por', 'Relacionada', 'Epic'
  linkedIssueKey: string;
  linkedIssueSummary?: string;
}

export interface JiraFields {
  summary: string;
  description: string;
  issueType: JiraIssueType;
  status: string; // 'Tareas por hacer', 'Planificación', 'IN DEV', 'En curso', 'Pull request', 'Ready for QA', 'In QA', 'Done', 'On Hold'
  storyPoints?: number;
  priority: 'Muy Alta' | 'Alta' | 'Media' | 'Baja' | 'P0' | 'P1';
  assignee?: JiraUser;
  reporter?: JiraUser;
  sprint?: string;
  /** Declared codebase / repo labels from Atlassian components. */
  components: string[];
  /** Parent epic issue key when known (team-managed parent or classic Epic Link). */
  parentEpicKey?: string;
  parentEpicSummary?: string;
  fixVersion?: string;
  labels: string[];
  acceptanceCriteria: JiraAcceptanceCriterion[];
  subtasks: JiraSubtask[];
  issueLinks: JiraIssueLink[];
  updatedAt: string; // ISO 8601
  createdAt: string; // ISO 8601
}

export interface ZuneloAttentionMeta {
  status: AttentionStatus;
  ai: string;
  next: string;
  tags?: string[];
  refs?: CrossReference[];
}

export interface JiraIssue {
  id: string;
  key: string;
  self: string;
  fields: JiraFields;
  /** Optional BFF enrichment used by Zunelo to restore presentation hints. */
  zuneloMeta?: ZuneloAttentionMeta;
}

// ==========================================
// 3. GitHub Real Domain Contracts
// ==========================================

export interface GitHubUser {
  id: number;
  login: string;
  name?: string;
  avatarUrl?: string;
  initials?: string;
  color?: string;
  personId?: string;
}

export interface GitHubBranchRef {
  ref: string; // 'wt/carrier-severity'
  sha: string;
  repo: string; // 'genlogs/carrier-web'
}

export type GitHubReviewState =
  'Aprobado' | 'Cambios solicitados' | 'Pendiente' | 'Te piden revisión';

export interface GitHubReviewer {
  user: GitHubUser;
  state: GitHubReviewState;
  requestedAt?: string; // ISO 8601
  reviewedAt?: string; // ISO 8601
  isCurrentUser?: boolean;
}

export interface GitHubDiffHunkLine {
  type: 'ctx' | 'del' | 'add';
  content: string;
}

export interface GitHubComment {
  id: string;
  user: GitHubUser;
  body: string;
  path?: string;
  line?: number;
  createdAt: string; // ISO 8601
  resolved: boolean;
  state?: 'Sin resolver' | 'Resuelto' | 'Aprobado';
  diffHunk?: GitHubDiffHunkLine[];
}

export interface GitHubCheckRun {
  id?: string;
  name: string;
  status?: 'queued' | 'in_progress' | 'completed' | 'ok' | 'warn' | 'fail' | string;
  conclusion?: 'ok' | 'warn' | 'fail' | 'skipped' | 'passed' | string;
  durationSeconds?: number;
  durationFormatted?: string; // '1m 42s'
  time?: string;
  title?: string;
  summary?: string;
  details?: string[];
  summaryLines?: string[];
  startedAt?: string; // ISO 8601
  completedAt?: string; // ISO 8601
}

export interface GitHubChangedFile {
  filename: string;
  additions: number;
  deletions: number;
  changes: number;
}

export interface GitHubPullRequest {
  id: string;
  number: number;
  title: string;
  bodyLines: string[];
  state: 'open' | 'closed' | 'merged';
  merged: boolean;
  /** Canonical github.com browse URL for the pull request. */
  htmlUrl?: string;
  head: GitHubBranchRef;
  base: GitHubBranchRef;
  author: GitHubUser;
  assignees: GitHubUser[];
  reviewers: GitHubReviewer[];
  comments: GitHubComment[];
  checkRuns: GitHubCheckRun[];
  changedFiles: GitHubChangedFile[];
  totalAdditions: number;
  totalDeletions: number;
  commitsCount: number;
  createdAt: string; // ISO 8601
  updatedAt: string; // ISO 8601
  linkedTicketKey?: string;
  /** Merge method used the last time this PR was merged (squash/merge/rebase). */
  lastMergeStrategy?: 'squash' | 'merge' | 'rebase';
  /** Optional BFF enrichment used by Zunelo to restore presentation hints. */
  zuneloMeta?: ZuneloAttentionMeta;
}

// ==========================================
// 4. Slack Real Domain Contracts
// ==========================================

export interface SlackUser {
  id: string;
  name: string;
  realName: string;
  personId?: string;
  avatarUrl?: string;
  initials: string;
  color?: string;
}

export interface SlackReaction {
  name: string;
  count: number;
  users?: string[];
}

export interface SlackMessage {
  ts: string; // epoch.sequence timestamp e.g. "1723630680.000100"
  user: SlackUser;
  text: string;
  timeFormatted: string; // "11:58"
  isoTime: string; // ISO 8601
  tag?: string; // "te menciona"
  divider?: boolean;
  codeSnippet?: string;
  repliesCount?: number;
  lastReplyTime?: string;
  reactions: SlackReaction[];
}

export interface SlackChannel {
  id: string;
  name: string; // "#eng-carrier"
  topic?: string;
}

export interface SlackThread {
  threadTs: string;
  channel: SlackChannel;
  messages: SlackMessage[];
  participants: SlackUser[];
  unread: boolean;
  mentionsCurrentUser: boolean;
  /** Named in channel text without a Slack `<@U…>` mention. */
  implicitlyAddressesUser?: boolean;
  /** Serialized `kind:value` competence reasons when the thread matched the user's profile. */
  competenceReasons?: string[];
  /** Slack user id of the newest message. */
  latestAuthorId?: string;
  /** Which side must act next: the user ('me') or the other side ('them'). */
  ballInCourt?: 'me' | 'them';
  linkedTicketKey?: string;
  tags?: string[];
  aiSummary?: {
    summary: string;
    actionItem: string;
    questions?: string[];
  };
  updatedAt: string; // ISO 8601
  /** Stable attention-card id from the live Slack BFF / attention API. */
  attentionId?: string;
  /** Permalink back to the Slack conversation when available. */
  permalink?: string;
  /** False for list stubs; true after conversations.replies hydration. */
  messagesHydrated?: boolean;
  /** True when Slack reported a delete/edit; client should re-hydrate. */
  transcriptStale?: boolean;
  /** Optional BFF enrichment used by Zunelo to restore presentation hints. */
  zuneloMeta?: ZuneloAttentionMeta;
}

// ==========================================
// 5. Autonomous Agent Domain Contracts
// ==========================================

export type AgentExecutionState = 'corriendo' | 'en pausa' | 'completado' | 'fallido';

export interface AgentLogEntry {
  id: string;
  timestamp: string; // ISO 8601 or HH:mm:ss
  level: 'info' | 'warn' | 'success' | 'cmd';
  message: string;
}

export interface AgentSession {
  sessionId?: string;
  agentName?: string; // 'Claude Code', 'Codex', 'OpenCode'
  name?: string;
  ticket?: string;
  state: AgentExecutionState;
  running: boolean;
  targetTicketKey?: string;
  workingBranch?: string;
  branch?: string;
  lastActivitySummary?: string;
  last?: string;
  elapsedSeconds?: number;
  elapsedFormatted?: string; // '12 min'
  elapsed?: string;
  tokensConsumed?: number;
  tokensFormatted?: string; // '184k tok'
  tokens?: string;
  costUsd?: number;
  costFormatted?: string; // '$2.40'
  cost?: string;
  secondAction?: string;
  logs?: AgentLogEntry[];
  startedAt?: string; // ISO 8601
  updatedAt?: string; // ISO 8601
}

// ==========================================
// 6. Gmail Domain Contracts
// ==========================================

export interface GmailParticipant {
  name: string;
  email: string;
  initials?: string;
  color?: string;
}

export interface GmailMessage {
  id: string;
  threadId: string;
  from: GmailParticipant;
  to: string;
  subject: string;
  bodyParagraphs: string[];
  quotedHead?: string;
  quotedText?: string;
  kind: 'Interno' | 'Aviso' | 'Externo';
  unread: boolean;
  blocksWork: boolean;
  linkedTicketKey?: string;
  receivedAt: string; // ISO 8601
  /** Optional pre-computed AI summary, mirroring SlackThread.aiSummary. */
  aiSummary?: {
    summary: string;
    actionItem: string;
  };
}

export interface GmailThread {
  id: string;
  messages: GmailMessage[];
  subject: string;
  unread: boolean;
  blocksWork: boolean;
  kind: 'Interno' | 'Aviso' | 'Externo';
  receivedAt: string; // ISO 8601
  linkedTicketKey?: string;
}

// ==========================================
// 7. Workspace Tool Domains (Daily, Reminders, Notes)
// ==========================================

export interface DailyLine {
  id: string;
  key?: string;
  text: string;
  active: boolean;
  link?: string;
  isCustom?: boolean;
}

export interface DailyGroup {
  id: 'ayer' | 'hoy' | 'bloq';
  title: string;
  tone: Tone;
  lines: DailyLine[];
  draftText: string;
}

export interface ReminderItem {
  id: string;
  text: string;
  done: boolean;
  link?: string;
  title?: string;
  content?: string;
  due?: string; // YYYY-MM-DD
  time?: string; // HH:mm
  isoDue?: string; // ISO 8601
}

export interface NoteItem {
  id: string;
  text: string;
  title?: string;
  content?: string;
  link?: string;
  createdAt: string; // ISO 8601
  due?: string; // YYYY-MM-DD
  time?: string; // HH:mm
}

// ==========================================
// 8. Cross-Context & Signals
// ==========================================

export interface SentryOrExtSignal {
  src: 'Sentry' | 'Moesif' | 'AWS' | 'Datadog' | string;
  label: string;
  meta: string;
  tone: Tone;
  url?: string;
}

export interface HoldStep {
  label: string;
  tone: Tone;
}

export interface HoldInfo {
  kind: 'release' | 'infra' | 'external' | 'product';
  chip: string;
  title: string;
  note: string;
  steps: HoldStep[];
}

export interface StateSuggestion {
  to: string;
  why: string;
}

export interface CrossReference {
  key: string;
  grade: 'explícita' | 'inferida';
  why: string;
}

// ==========================================
// 9. Unified Attention View Model (for Radar & Cards)
// ==========================================

export interface AttentionItem {
  id: string;
  source: SourceType;
  st: AttentionStatus;
  primary: string;
  secondary: string;
  title: string;
  ai: string;
  next: string;
  /** Optional footer CTA label (Jira/GitHub). Slack uses composer Enviar instead. */
  action?: string;
  age: string;
  updatedAt?: string; // ISO 8601

  // Specific domain bindings
  ticket?: string;
  workflow?: string;
  type?: JiraIssueType | string;
  desc?: string;
  criteria?: JiraAcceptanceCriterion[];
  subtasks?: JiraSubtask[];
  labels?: string[];
  tags?: string[];
  refs?: CrossReference[];
  /** Declared Jira components (repo/project labels). */
  components?: string[];
  /** Parent epic key for hierarchy context chips. */
  parentEpicKey?: string;
  parentEpicSummary?: string;

  // GitHub specific
  role?: 'author' | 'reviewer';
  kind?: string;
  merged?: boolean;
  head?: string;
  base?: string;
  add?: number;
  del?: number;
  commits?: string;
  files?: GitHubChangedFile[];
  checks?: GitHubCheckRun[];
  reviewers?: GitHubReviewer[];
  comments?: GitHubComment[];
  mergeTitle?: string;
  mergeText?: string;
  mergeCta?: string;
  body?: string[];
  /** External github.com URL when known (open PR in browser). */
  htmlUrl?: string;

  // Slack specific
  threadMeta?: string;
  members?: SlackUser[];
  messages?: SlackMessage[];

  // Gmail specific
  unread?: boolean;
  blocksWork?: boolean;
  fromName?: string;
  fromEmail?: string;
  initials?: string;
  color?: string;
  to?: string;
  quotedHead?: string;
  quoted?: string;
  gmailMessages?: GmailMessage[];

  // Raw domain entities (strongly typed)
  rawJiraIssue?: JiraIssue;
  rawPullRequest?: GitHubPullRequest;
  rawSlackThread?: SlackThread;
  rawAgentSession?: AgentSession;
  rawGmailMessage?: GmailMessage;
  rawGmailThread?: GmailThread;
}

// Backwards compatibility aliases
export type AcceptanceCriterion = JiraAcceptanceCriterion;
export type CheckRun = GitHubCheckRun;

export type AgentItem = AgentSession & {
  name: string;
  ticket: string;
  last: string;
  elapsed: string;
  tokens: string;
  cost: string;
  secondAction: string;
};

// ==========================================
function toDate(input: string | number | Date): Date {
  return typeof input === 'string' || typeof input === 'number' ? new Date(input) : input;
}

function formatPastDiff(diffMins: number, diffHours: number, diffDays: number): string {
  if (diffMins < 1) return 'ahora mismo';
  if (diffMins < 60) return `hace ${diffMins}m`;
  return formatPastByHours(diffHours, diffDays);
}

function formatPastByHours(diffHours: number, diffDays: number): string {
  if (diffHours < 24) return `hace ${diffHours}h`;
  if (diffDays === 1) return 'ayer';
  return `hace ${diffDays}d`;
}

function parseDateInput(dateInput: string | number | Date): Date | null {
  const date = toDate(dateInput);
  if (!date || isNaN(date.getTime())) return null;
  return date;
}

function resolveNowMs(nowInput: string | number | Date): number {
  const now = toDate(nowInput);
  return isNaN(now.getTime()) ? Date.now() : now.getTime();
}

function invalidDateFallback(dateInput: string | number | Date): string {
  return typeof dateInput === 'string' ? dateInput : 'reciente';
}

function isMissingDateInput(dateInput: string | number | Date): boolean {
  return dateInput !== 0 && !dateInput;
}

export function formatRelativeTime(
  dateInput: string | number | Date,
  nowInput: string | number | Date = Date.now(),
): string {
  if (isMissingDateInput(dateInput)) return 'reciente';

  const date = parseDateInput(dateInput);
  if (!date) return invalidDateFallback(dateInput);

  const diffMs = resolveNowMs(nowInput) - date.getTime();
  if (diffMs < 0) return 'en el futuro';

  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  return formatPastDiff(diffMins, diffHours, diffDays);
}
